/**
 * Types and pure helpers for the modular Campaign feature.
 *
 * A Campaign is a hierarchical, reusable definition — a tree of CampaignStep nodes — tracked two
 * independent ways:
 *   - enablement: per-SE, completion-oriented (CampaignEnrollment rolls up the SE's journey-map /
 *     assessment progress, computed server-side and never stored);
 *   - delivery: customer-facing, status-driven (CampaignDelivery → CampaignDeliveryTarget →
 *     CampaignDeliveryStepStatus, plus CampaignActionItem follow-ups).
 *
 * Everything here is backed by generic toolbox_db CRUD (`db/campaign`, `db/campaign-step`,
 * `db/campaign-enrollment`, `db/campaign-delivery`, `db/campaign-delivery-target`,
 * `db/campaign-delivery-step-status`, `db/campaign-action-item`), so the shapes mirror
 * `db/models/campaign.py` exactly — snake_case fields, foreign keys as their integer pk, and a
 * write is a full-record PUT (never PATCH). No case-mapping layer.
 *
 * The helpers are deliberately pure (no fetching, no React) so both the resource surface and the
 * admin surface can share them.
 */

// ---------------------------------------------------------------------------
// Enums, mirrored from db/models/campaign.py
// ---------------------------------------------------------------------------

export type CampaignStatus = 'draft' | 'published' | 'archived';

export const CAMPAIGN_STATUSES: { value: CampaignStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

/**
 * A campaign-level visual theme. Selecting one retints every object rendered inside the campaign
 * (chrome, the frontier runner, and the MUI report) — read from `Campaign.metadata.theme`.
 *   - `harbor`      → the @harbor/elements magnetic LIGHT palette (--hbr-* tokens);
 *   - `harbor-dark` → the @harbor/elements magnetic DARK palette;
 *   - `ai-era`      → the dark-navy palette used by the AI-Era Cyber Defense Assessment report.
 * Absent/unknown falls back to the Cyber Academy default tokens (`guided/styles/tokens.css`).
 */
export type CampaignTheme = 'harbor' | 'harbor-dark' | 'ai-era';

export const CAMPAIGN_THEMES: { value: CampaignTheme; label: string }[] = [
  { value: 'harbor', label: 'Harbor (light)' },
  { value: 'harbor-dark', label: 'Harbor Dark' },
  { value: 'ai-era', label: 'AI-Era Cyber Defense' },
];

/**
 * An optional author-supplied help/resource link — the replacement for the frontier runner's
 * hard-coded `trainingVideoUrl`. A campaign and each step can carry an array of these in
 * `metadata.helpLinks`; the runner renders one control per link and shows nothing at all when the
 * array is empty/absent (never a placeholder).
 */
export interface HelpLink {
  label: string;
  url: string;
}

export type StepTrack = 'enablement' | 'delivery' | 'followup';

export const STEP_TRACKS: { value: StepTrack; label: string }[] = [
  { value: 'enablement', label: 'Enablement' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'followup', label: 'Follow-up' },
];

export type StepKind =
  | 'group'
  | 'journey_map'
  | 'assessment'
  | 'moodle_workshop'
  | 'action_item'
  | 'followup'
  | 'content'
  | 'salesforce_link'
  | 'question';

export const STEP_KINDS: { value: StepKind; label: string }[] = [
  { value: 'group', label: 'Group' },
  { value: 'journey_map', label: 'Journey Map' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'moodle_workshop', label: 'Moodle Workshop' },
  { value: 'action_item', label: 'Action Item' },
  { value: 'followup', label: 'Follow-up' },
  { value: 'content', label: 'Content' },
  { value: 'salesforce_link', label: 'Salesforce Selector' },
  { value: 'question', label: 'Questions' },
];

/**
 * How a step is completed, orthogonal to `track`/`step_kind` (mirrors CampaignStep.completion_mode):
 *   - `once`: a one-time completion for the SE's run of the campaign (the enablement signal, a
 *     self-attest tick, a question answer set, or a Salesforce link on the enrollment);
 *   - `per_delivery`: the step is delivered repeatedly and each delivery is tracked independently
 *     through CampaignDelivery → CampaignDeliveryStepStatus (a workshop/event run for many customers).
 */
export type CompletionMode = 'once' | 'per_delivery';

export const COMPLETION_MODES: { value: CompletionMode; label: string }[] = [
  { value: 'once', label: 'One-time completion' },
  { value: 'per_delivery', label: 'Per delivery (repeatable)' },
];

export type MapKind = 'standard' | 'composite';

export type EnrollmentStatus = 'not_started' | 'in_progress' | 'completed';

export type AudienceMode = 'event' | 'single_customer';

export const AUDIENCE_MODES: { value: AudienceMode; label: string }[] = [
  { value: 'event', label: 'Event (one-to-many)' },
  { value: 'single_customer', label: 'Single customer' },
];

export type DeliveryStatus =
  | 'draft'
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'cancelled';

export const DELIVERY_STATUSES: { value: DeliveryStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export type TargetStatus = 'not_started' | 'in_progress' | 'completed' | 'cancelled';

export type StepStatus = 'not_started' | 'in_progress' | 'completed' | 'skipped';

export const STEP_STATUSES: { value: StepStatus; label: string }[] = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'skipped', label: 'Skipped' },
];

export type ActionItemKind = 'action_item' | 'followup';

export type ActionItemStatus = 'open' | 'in_progress' | 'done' | 'dropped';

export type ReportStatus = 'draft' | 'final';

export const REPORT_STATUSES: { value: ReportStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'final', label: 'Final' },
];

export const ACTION_ITEM_STATUSES: { value: ActionItemStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
  { value: 'dropped', label: 'Dropped' },
];

/** Bag of arbitrary JSON on every model's `metadata` column. */
export type Json = Record<string, any>;

// ---------------------------------------------------------------------------
// Record shapes
// ---------------------------------------------------------------------------

/** Fields present on every CreatedTimestampedModel row. */
interface TimestampedRow {
  id: number;
  created?: string;
  last_updated?: string;
  updated_by?: string | null;
}

export interface Campaign extends TimestampedRow {
  key: string;
  slug: string;
  name: string;
  description: string | null;
  image_url: string | null;
  status: CampaignStatus;
  version: number;
  owner: string | null;
  estimated_duration_minutes: number | null;
  audience_rules: Json;
  metadata: Json;
  /**
   * Which front-end runner renders this campaign. `data-driven` (default) renders purely from the DB
   * content tables; a named runner (e.g. `frontier`) selects a vendored bespoke runner via the client
   * runner registry — this is what lets Frontier AI be "the first campaign, not the only one".
   */
  runner_key: string;
  /** Top-level AI system prompt; the AI seam is scoped to the phase-6 report (see CAMPAIGN_MODULARITY_SPEC). */
  assistant_system_prompt: string | null;
  /** Scoring/band weights for deep-dive readouts, opaque to the API. */
  scoring_config: Json;
  is_active: boolean;
  available_from: string | null;
  available_to: string | null;
}

export interface CampaignStep extends TimestampedRow {
  campaign: number;
  parent: number | null;
  order: number;
  name: string;
  description: string | null;
  is_required: boolean;
  /** When true, the runner blocks the "Next" control past this step until its activity is complete. */
  blocks_progression: boolean;
  /**
   * Authoring visibility. When true the step (and, for a `group`, its whole subtree) is hidden from
   * the runner without being deleted; the admin builder still shows it. See `visibleSteps`.
   */
  hidden?: boolean;
  estimated_duration_minutes: number | null;
  track: StepTrack;
  step_kind: StepKind;
  /** One-time vs. repeated-per-delivery tracking — see CompletionMode. */
  completion_mode: CompletionMode;
  journey_map: number | null;
  assessment: number | null;
  child_type: string | null;
  child_id: string | null;
  launch_url: string | null;
  provider: string | null;
  provider_config: Json;
  metadata: Json;
  // Read-only denormalizations so a step list renders without joining the template tables.
  journey_map_name?: string | null;
  journey_map_kind?: MapKind | null;
  /** The linked journey map's own description (distinct from the step's `description`). */
  journey_map_description?: string | null;
  assessment_name?: string | null;
}

/** A CampaignStep with its subtree attached — built client-side from `parent`/`order`. */
export interface CampaignStepNode extends CampaignStep {
  children: CampaignStepNode[];
}

/** The computed rollup on CampaignEnrollment.progress (db/services/campaigns.progress_summary). */
export interface CampaignProgress {
  percent: number;
  counts: {
    total: number;
    completed: number;
    journey_maps: number;
    assessments: number;
  };
  status: string;
}

export interface CampaignEnrollment extends TimestampedRow {
  campaign: number;
  user: number;
  status: EnrollmentStatus;
  started_at: string | null;
  completed_at: string | null;
  /**
   * FK to the local `Customer` mirror row (PK = Salesforce AccountId). The account this SE bound to
   * their run via a `salesforce_link` step. Null until linked; the authoritative name is resolved
   * from Salesforce at read time, never stored.
   */
  customer: string | null;
  /**
   * FK to the local `Opportunity` mirror row (PK = Salesforce OpportunityId). Null when linked with
   * "Continue without Opportunity". Name resolved from Salesforce at read time, never stored.
   */
  opportunity: string | null;
  metadata: Json;
  user_email?: string | null;
  progress?: CampaignProgress;
}

/**
 * The authoritative completion status for an enrollment: the server-computed, composite-aware rollup
 * (`progress.status`, from db/services/campaigns.progress_summary — mirrored in Go computeProgress)
 * when it was served, falling back to the stored `status` column only when no rollup is present.
 *
 * The stored `status` column is written ONLY client-side by the runner's whole-record PUT and lags the
 * derived rollup — a step (esp. a composite journey map) can complete without the runner ever PUTting
 * the enrollment to 100%. Reporting MUST classify on the derived value, or completed enrollments read
 * as not-started (the "reporting shows 0 completions" bug). See [[campaign-completion-source-of-truth]].
 */
export function enrollmentStatus(e: CampaignEnrollment): EnrollmentStatus {
  const derived = e.progress?.status;
  if (derived === 'not_started' || derived === 'in_progress' || derived === 'completed') {
    return derived;
  }
  return e.status;
}

export interface CampaignDelivery extends TimestampedRow {
  campaign: number;
  name: string | null;
  audience_mode: AudienceMode;
  status: DeliveryStatus;
  owner: string | null;
  scheduled_for: string | null;
  completed_at: string | null;
  notes: string | null;
  metadata: Json;
  campaign_key?: string;
  campaign_name?: string;
  /**
   * The SE-user link resolved from the free-text `owner` email (migration 0161). Writable FK id; the
   * two `owner_sales_level_*` reads below are the denormalized Cisco territory of that user, used to
   * group deliveries by sales level. All three are null until the owner resolves to a User.
   */
  owner_user?: number | null;
  owner_sales_level_1?: string | null;
  owner_sales_level_2?: string | null;
}

/**
 * The feedback an SE gives when they mark a delivery complete (via the runner's "Complete Delivery"
 * button). Persisted as `delivery.metadata.feedback` — no dedicated column/migration — so the whole
 * complete-with-feedback flow ships frontend-only.
 */
export interface DeliveryFeedback {
  /** 1–5 overall rating, or null if the SE left it blank. */
  rating: number | null;
  /** Free-text comment (what went well / could improve). */
  comment: string;
  submitted_at: string;
  submitted_by: string | null;
}

/**
 * A delivery is "complete" once the SE explicitly finishes it (the "Complete Delivery" button sets
 * `status='completed'` + `completed_at`). This is distinct from the *derived* per-step progress tone
 * a row shows — an SE can finish a delivery without every optional step being green.
 */
export const isDeliveryComplete = (d: CampaignDelivery): boolean =>
  d.status === 'completed' || !!d.completed_at;

/**
 * A cancelled delivery is kept (not deleted) but drops out of the "Active" list and out of the
 * per-delivery-group completion metric — it neither counts as done nor holds the group in progress.
 */
export const isDeliveryCancelled = (d: CampaignDelivery): boolean => d.status === 'cancelled';

/** The stored feedback object on a delivery, if the SE left any on completion. */
export const deliveryFeedback = (d: CampaignDelivery): DeliveryFeedback | null => {
  const fb = (d.metadata as any)?.feedback;
  return fb && typeof fb === 'object' ? (fb as DeliveryFeedback) : null;
};

/**
 * The SE's manual sort position for a delivery, stored in `metadata.order` by the "Manage deliveries"
 * drag-reorder. Unordered deliveries sort last (Infinity) so they fall back to newest-first.
 */
export const deliveryOrder = (d: CampaignDelivery): number => {
  const o = (d.metadata as any)?.order;
  return typeof o === 'number' ? o : Number.POSITIVE_INFINITY;
};

/** Deliveries in the SE's chosen manual order, falling back to newest-first for unordered ones. */
export const sortDeliveries = (list: CampaignDelivery[]): CampaignDelivery[] =>
  [...list].sort((a, b) => {
    const oa = deliveryOrder(a);
    const ob = deliveryOrder(b);
    if (oa !== ob) return oa - ob;
    return (b.created || '').localeCompare(a.created || '') || b.id - a.id;
  });

export interface CampaignDeliveryTarget extends TimestampedRow {
  delivery: number;
  /** FK to the local `Customer` mirror row (PK = Salesforce AccountId). Null until confirmed. */
  customer: string | null;
  /** Transient display label; the authoritative account name is resolved from Salesforce. */
  customer_name: string | null;
  /**
   * FK to the local `Opportunity` mirror row (PK = Salesforce OpportunityId). Null when the account
   * was linked with "Continue without Opportunity" — an opportunity can be attached later.
   */
  opportunity: string | null;
  status: TargetStatus;
  metadata: Json;
}

export interface CampaignDeliveryStepStatus extends TimestampedRow {
  delivery: number;
  step: number;
  target: number | null;
  status: StepStatus;
  completed_at: string | null;
  notes: string | null;
  metadata: Json;
  step_name?: string;
  step_kind?: StepKind;
}

export interface CampaignActionItem extends TimestampedRow {
  delivery: number | null;
  target: number | null;
  step: number | null;
  kind: ActionItemKind;
  text: string;
  owner: string | null;
  due_date: string | null;
  status: ActionItemStatus;
  order: number;
  metadata: Json;
}

/**
 * The report generated at the end of a campaign, persisted rather than computed on read (a change
 * from the enrollment-progress precedent — see db/models/campaign.py). `enrollment` is null for a
 * campaign-wide report; `payload` holds the report document itself.
 */
/**
 * What a report (its sections/figures, and a persisted `CampaignReport`) is bound to — so a campaign
 * can author several reports and bind each to a reference (see db/models/campaign.py
 * REPORT_BIND_SCOPE_CHOICES). Rows sharing one `(bind_scope, report_key)` under a campaign make up one
 * report; the resolvers select the matching set. The default `('campaign', '')` is the campaign's
 * primary report — what every pre-binding row resolves as.
 */
export type ReportBindScope = 'campaign' | 'step' | 'delivery' | 'global';

/** The binding fields shared by report sections, figures and persisted reports. */
export interface ReportBinding {
  bind_scope: ReportBindScope;
  report_key: string;
  /** Set only for `step` scope — the `CampaignStep` id the report is bound to. */
  step: number | null;
  /** Set only for `delivery` scope — the `CampaignDelivery` id the report is bound to. */
  delivery: number | null;
}

export interface CampaignReport extends TimestampedRow, ReportBinding {
  campaign: number;
  enrollment: number | null;
  title: string;
  kind: string;
  status: ReportStatus;
  generated_at: string | null;
  payload: Json;
  metadata: Json;
}

export type WorkshopMeetingStatus =
  | 'none'
  | 'scheduling'
  | 'scheduled'
  | 'failed'
  | 'cancelled';

/**
 * The workshop meeting booked on a campaign delivery's `prepare` step — replaces the earlier
 * `CampaignDeliveryStepStatus.metadata.workshopPrep` blob with real columns. Keyed like a step-status
 * row (`delivery`, `step`, `target` null = event-wide). The Webex meeting fields and roll-up counts are
 * columns; the direct-team and participant PersonRefs live in `metadata` (team/participants) so no
 * names are mirrored in queryable columns (resolved from Salesforce/Graph on read, PII stays in JSON).
 */
export interface CampaignWorkshopMeeting extends TimestampedRow {
  delivery: number;
  step: number;
  target: number | null;
  title: string | null;
  status: WorkshopMeetingStatus;
  webex_order_id: string | null;
  webex_meeting_id: string | null;
  web_link: string | null;
  start_time: string | null;
  end_time: string | null;
  duration_minutes: number | null;
  timezone: string | null;
  team_member_count: number;
  participant_count: number;
  scheduled_at: string | null;
  cancelled_at: string | null;
  metadata: Json;
}

/** Priority of a customer persona the workshop wants — advice to the champion, not a hard gate. */
export type PersonaRole = 'must' | 'should' | 'nice';

/**
 * A customer-side persona the campaign's workshop wants in the room, scoped to one campaign
 * (see db/models/campaign.py CampaignCustomerPersona). Replaces the panel's former static
 * `STAKEHOLDERS` list. `hidden` rows are retained in the DB but filtered from the runner view.
 */
/**
 * A customer-side persona a workshop wants in the room. **Global** since the 2026-09-25 globalization:
 * personas are no longer owned by one campaign — a campaign references a subset through
 * `CampaignCustomerPersonaLink`. The `campaign` FK was removed from this row.
 */
export interface CampaignCustomerPersona extends TimestampedRow {
  label: string;
  role: PersonaRole;
  why: string;
  order: number;
  hidden: boolean;
}

/**
 * A named progression stage of a campaign, scoped to one campaign (see db/models/campaign.py
 * CampaignPhase). The generic replacement for the guided runner's hard-coded `Phase` enum +
 * `PHASE_LABEL` + `PHASE_ORDER`: `key` is the stable machine id each step's `phase` references,
 * `label` is the heading shown in progress UI, and `order` is the unlock/progress order. `hidden`
 * rows are retained but filtered from the runner view. Empty (un-seeded) → the runner falls back to
 * its bundled phase constants.
 */
export interface CampaignPhase extends TimestampedRow {
  campaign: number;
  key: string;
  label: string;
  order: number;
  hidden: boolean;
  metadata?: Record<string, unknown>;
}

/**
 * A campaign's AI flow definition, scoped to one campaign (see db/models/campaign.py CampaignPrompt).
 * The generic replacement for the guided runner's hard-coded AI prompt strings: `flow_key` is the
 * stable machine id the runtime resolves a flow by, `template` is the prompt body with `{token}`
 * placeholders the flow fills from live grounding, `output_schema`/`model`/`grounding_inputs` carry
 * optional structured-output, model override, and declarative grounding hints. `hidden` rows are
 * retained but filtered from the runner view. Empty (un-seeded) → the runner falls back to its
 * bundled prompt templates.
 */
export interface CampaignPrompt extends TimestampedRow {
  campaign: number;
  flow_key: string;
  label: string;
  template: string;
  output_schema?: Record<string, unknown>;
  model?: string | null;
  grounding_inputs?: string[];
  order: number;
  hidden: boolean;
  metadata?: Record<string, unknown>;
}

/**
 * The access role a group has on a campaign (see db/models/campaign.py CampaignGroupAssignment):
 * `user` = a group whose members may see/run the campaign; `admin` = a group whose members
 * administer it. The two are stored as separate join rows so a group can hold either or both.
 */
export type CampaignGroupAccess = 'user' | 'admin';

export const CAMPAIGN_GROUP_ACCESS_USER: CampaignGroupAccess = 'user';
export const CAMPAIGN_GROUP_ACCESS_ADMIN: CampaignGroupAccess = 'admin';

/**
 * A row linking a campaign to a `UserGroup` with an access role (see db/models/campaign.py
 * CampaignGroupAssignment; unique on campaign+user_group+access). The campaign builder's dual group
 * multi-select writes/deletes these rows: the USER list controls who can see/run the campaign, the
 * ADMIN list controls who administers it. Server-side scoping enforcement is deferred (design gap).
 */
export interface CampaignGroupAssignment extends TimestampedRow {
  campaign: number;
  user_group: number;
  access: CampaignGroupAccess;
  metadata?: Record<string, unknown>;
}

/**
 * A Cisco-side direct-team role the campaign's workshop can be staffed from, scoped to one campaign
 * (see db/models/campaign.py CampaignWorkshopTeamRole). Replaces the panel's former static
 * `TEAM_ROLES` literal. `hidden` rows are retained in the DB but filtered from the runner view.
 */
export interface CampaignWorkshopTeamRole extends TimestampedRow {
  name: string;
  /** Short "what this role does in the workshop" note, shown in the prep panel's role legend (mirrors CampaignCustomerPersona.why). */
  description: string;
  order: number;
  hidden: boolean;
}

/**
 * The audience an outcome (and a pool question that explores it) belongs to. Kept distinct everywhere
 * downstream so AI context/suggestion generation and readouts never fold an internal (Cisco-side, e.g.
 * pipeline-building) goal into a customer-facing readout, and vice versa (see db/models/campaign.py
 * OUTCOME_CONTEXT_CHOICES).
 */
export type OutcomeContext = 'customer' | 'internal';

export const OUTCOME_CONTEXTS: { value: OutcomeContext; label: string }[] = [
  { value: 'customer', label: 'Customer-facing' },
  { value: 'internal', label: 'Internal (Cisco)' },
];

export const outcomeContextLabel = (ctx: string): string =>
  OUTCOME_CONTEXTS.find((c) => c.value === ctx)?.label ?? ctx;

/**
 * A prioritized intended outcome for a campaign — what the motion is meant to achieve (see
 * db/models/campaign.py CampaignOutcome). `order` is the drag rank, lowest on top = highest priority;
 * `context` keeps the customer/internal split; `hidden` retains the row while excluding it from AI
 * context/generation. The AI aligns its suggestions/framing to these and draws questions from the pool
 * (`CampaignQuestion`) based on which outcomes already look achieved.
 */
export interface CampaignOutcome extends TimestampedRow {
  title: string;
  description: string;
  context: OutcomeContext;
  order: number;
  hidden: boolean;
  metadata: Json;
}

/**
 * A question in a campaign's pool the AI can draw from (see db/models/campaign.py CampaignQuestion).
 * `outcome` links it to the outcome it explores (null = general pool); `context` mirrors the outcome's
 * customer/internal split; `order` ranks within the pool; `hidden` excludes it from selection. Customer
 * answers are not stored here — they live with the delivery's `question` step and feed the deep-dive /
 * readout AI context alongside the pool.
 */
export interface CampaignQuestion extends TimestampedRow {
  /** FK into the (now global) CampaignOutcome table; null = general pool question. */
  outcome: number | null;
  text: string;
  context: OutcomeContext;
  order: number;
  hidden: boolean;
  metadata: Json;
}

/**
 * A single capability/play/product in a campaign's catalogue — the one source of truth (see
 * db/models/campaign.py CampaignCapability). De-dupes the frontier runner's three inlined copies
 * (`GUIDE_PLAYS`, `DEEP_DIVE_PLAYS`, `PLAY_PROPOSALS`) into one row per `key`; content blocks,
 * questions and scenarios reference a capability by that `key` rather than repeating its prose.
 */
export interface CampaignCapability extends TimestampedRow {
  key: string;
  name: string;
  description: string;
  /** Rich (HTML) detail body. */
  detail: string;
  /** Report-ready recommendation text used when this capability is proposed. */
  proposal: string;
  resource_links: Array<{ label: string; url: string }>;
  order: number;
  hidden: boolean;
  metadata: Json;
}

/**
 * Reference-object membership link rows (globalization, 2026-09-25). Each links one campaign to one
 * global reference object of its type; a campaign's builder shows only the objects it links, and the
 * same object can be linked by many campaigns. The link row is pure membership — the object's own
 * fields (label/name/order/hidden/…) live on the object row.
 */
export interface CampaignCustomerPersonaLink extends TimestampedRow {
  campaign: number;
  persona: number;
}

export interface CampaignWorkshopTeamRoleLink extends TimestampedRow {
  campaign: number;
  role: number;
}

export interface CampaignOutcomeLink extends TimestampedRow {
  campaign: number;
  outcome: number;
}

export interface CampaignQuestionLink extends TimestampedRow {
  campaign: number;
  question: number;
}

export interface CampaignCapabilityLink extends TimestampedRow {
  campaign: number;
  capability: number;
}

/**
 * Generic, category-keyed, self-nesting campaign content (see db/models/campaign.py
 * CampaignContentBlock). One flexible row shape for the long tail of small content lists so a new
 * category needs no new table: `category` selects the family and the client types `payload`
 * per-category. `parent` gives generic hierarchy (lane → question, scenario → section → question).
 *
 * Predefined questions (discovery/guide/deep-dive) are NON-AI: their answer options/cues live in
 * `payload` (see `CampaignContentBlockPayload`) and the runner renders them directly. The lone AI
 * hook is the opt-in `ai_question_config` category (admin toggle), off by default.
 */
export interface CampaignContentBlock<P = CampaignContentBlockPayload> extends TimestampedRow {
  campaign: number;
  parent: number | null;
  category: string;
  key: string;
  label: string;
  order: number;
  hidden: boolean;
  payload: P;
  metadata: Json;
}

/** Predefined answer option for a question content block (a band the SE selects; never AI-driven). */
export interface CampaignQuestionOption {
  value: string;
  cues: string[];
  plays: string[];
}

/** Loosely-typed union of the shapes `CampaignContentBlock.payload` carries across categories. */
export type CampaignContentBlockPayload = {
  /** Question categories carry the ask + predefined options. */
  ask?: string;
  why?: string;
  roles?: string[];
  who?: string[];
  options?: CampaignQuestionOption[];
  bands?: Json;
  /** Opt-in AI question suggestion (category `ai_question_config`), off by default. */
  enabled?: boolean;
  pool_category?: string;
  suggest_count?: number;
  refinement_context?: string;
  // The payload is a heterogeneous per-category bag (strings, numbers, arrays, nested objects); the
  // typed fields above are the well-known ones. `unknown` (not `Json`, which is an object type) lets a
  // scalar field like `ask` coexist with the index signature; the runner's `resolveContent.ts` builders
  // narrow per category.
  [key: string]: unknown;
};

/**
 * A campaign's file object (deck, PDF, image, doc) stored in S3 as a swappable file reference (see
 * db/models/campaign.py CampaignAsset). The row holds only an `object_key`; bytes never transit the
 * API. `object_key` is blank until the file is uploaded (presign follows CampaignReportFigure).
 */
export interface CampaignAsset extends TimestampedRow {
  campaign: number;
  kind: string;
  key: string;
  title: string;
  object_key: string;
  content_type: string;
  size_bytes: number | null;
  order: number;
  hidden: boolean;
  metadata: Json;
}

/**
 * A generic, campaign-agnostic reference from a step to one of the campaign's file assets, stored on
 * the step's `metadata.files` (a `StepFileRef[]`). It names a `slot` — the role the file plays for
 * this step (author-chosen, e.g. `deck`) — and the `CampaignAsset` it points to by its stable `key`
 * (optionally narrowed by `kind`). The asset rows stay campaign-level and reusable; a step only
 * references them, so the same model works for any runner (or the generic step bodies), not just the
 * Frontier workshop decks.
 */
export interface StepFileRef {
  /** The role the referenced file plays for this step (author-chosen, generic — e.g. 'deck'). */
  slot: string;
  /** The `CampaignAsset.key` this points to (unique per (campaign, kind, key)). */
  assetKey: string;
  /** Optional `CampaignAsset.kind` to disambiguate the key (e.g. 'deck'). */
  kind?: string;
  /**
   * Optional author-editable, end-user-facing heading for this file in the step. When set the runner
   * uses it as the section's file heading (e.g. the workshop deck's "Session presentation" title) in
   * place of its bundled default, so the author can rename what the SE sees per step. Empty/absent →
   * the runner's bundled heading.
   */
  label?: string;
}

/**
 * A generic, campaign-agnostic reference from a step to one of the campaign's predefined question
 * content blocks, stored on the step's `metadata.questionRefs` (a `StepQuestionRef[]`). It mirrors
 * `StepFileRef` exactly, but points at a `CampaignContentBlock` question row instead of a file asset:
 * the question rows (categories like `recommended_question`, `scenario_question`, `deep_dive_question`,
 * `discovery_question`, `prereq_question`, `iq_scenario`, `question_bank`) stay campaign-level and
 * reusable — carrying their ask/options/cues in `payload` — and a step only *references* which ones it
 * uses by (category, key), so the same model works for any runner (or the generic step bodies). Editing
 * a referenced block's options/cues writes to the shared block, not to the step.
 */
export interface StepQuestionRef {
  /** Optional author-chosen role/label for this question in the step (generic — e.g. 'opener'). */
  slot?: string;
  /** The `CampaignContentBlock.category` this points to (e.g. 'recommended_question'). */
  category: string;
  /** The `CampaignContentBlock.key` this points to. */
  key: string;
}

/** Where a report section's body comes from (see db/models/campaign.py CampaignReportSection). */
export type ReportSectionSource = 'template' | 'generated';

/**
 * One section of a campaign's report template, scoped to one campaign (see db/models/campaign.py
 * CampaignReportSection). Promotes the frontier runner's former hard-coded `REPORT_SECTION_QUESTIONS`
 * / `DATA_SECTION` / `DEFAULT_HIDDEN_SECTIONS` constants into per-campaign, author-editable rows:
 * `section_id` matches the report engine's string section id, `ai_question` binds the stored prompt
 * that writes the section (null = deterministic drafter only), `is_data_section` marks the section
 * the workshop's captured data drafts into, and `hidden_by_default` seeds it switched-off.
 */
export interface CampaignReportSection extends TimestampedRow, ReportBinding {
  campaign: number;
  section_id: string;
  title: string;
  number: string;
  order: number;
  source: ReportSectionSource;
  is_data_section: boolean;
  hidden_by_default: boolean;
  /** The bound `ai_question` id, or null when the section is not AI-written. */
  ai_question: number | null;
  /** Read-only convenience fields the serializer joins from the bound question. */
  ai_question_title?: string | null;
  ai_question_prompt?: string | null;
  blocks: unknown[];
  /**
   * Admin-authored body used in place of AI generation when `ai_enabled` is false. Plain prose (one
   * paragraph per line); an SE may override it per delivery from the runner. Empty by default.
   */
  preloaded_content: string;
  /**
   * Whether this section auto-generates with AI when the report loads. True (default) preserves the
   * prior behavior; false seeds the section from `preloaded_content` and exposes a "Rewrite with AI"
   * button instead of generating on load.
   */
  ai_enabled: boolean;
  /** Grid geometry on the unified report canvas (x/w = cols out of 12, y/h = rows). Null on every
   * field = no geometry; the renderer back-fills a full-width block stacked in `order`. */
  layout_x: number | null;
  layout_y: number | null;
  layout_w: number | null;
  layout_h: number | null;
  metadata: Record<string, unknown>;
}

/** A report figure is either a chart or an uploaded image (see db/models/campaign.py). */
export type ReportFigureKind = 'chart' | 'image';
/** The chart visual style for a `chart` figure. */
export type ReportFigureChartKind = 'bar' | 'doughnut' | 'line';

/**
 * One figure (chart or image) placed on a campaign's admin-authored report canvas (see
 * db/models/campaign.py CampaignReportFigure). Admins arrange these once on a shared layout that
 * renders identically for every SE and delivery; per-delivery data fills the slot at render time.
 *
 * `figure_id` is a stable minted id (never derived from array position) used in the renderer's
 * `chartOrder` / `figureAnchors`. `order` is a flat sequence across all of a campaign's figures;
 * `anchor` is the gap index within `section_id` the figure attaches after (null = end of section).
 * For a chart, `chart_query` holds the tilebuilder query — a per-delivery Campaign Delivery query
 * (the renderer fills the delivery id) or a static `{ bars }`. For an image, `image_object_key` is
 * the S3 key of the uploaded asset (bytes never ride in enrollment JSON; a signed URL is minted on
 * read).
 */
export interface CampaignReportFigure extends TimestampedRow, ReportBinding {
  campaign: number;
  section_id: string;
  figure_id: string;
  kind: ReportFigureKind;
  order: number;
  anchor: number | null;
  title: string;
  caption: string;
  explanation: string;
  chart_kind: ReportFigureChartKind;
  tone: string;
  chart_query: Record<string, unknown>;
  /** Grid geometry on the unified report canvas (see CampaignReportSection). Null = back-fill after
   * the figure's section in `order`; populated = a placed grid cell coequal with prose sections. */
  layout_x: number | null;
  layout_y: number | null;
  layout_w: number | null;
  layout_h: number | null;
  image_object_key: string | null;
  image_content_type: string | null;
  image_width: number | null;
  image_height: number | null;
  image_size_bytes: number | null;
  metadata: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Whether a report section/figure/report row belongs to the report identified by `(scope, key)`.
 * The resolvers use this to select one report's rows out of a campaign that authors several. `step`
 * and `delivery` are treated as part of the key for their scopes so a step/delivery-bound report only
 * resolves for its own reference.
 */
export function matchesReportBinding(
  row: ReportBinding,
  scope: ReportBindScope,
  key = '',
  ref?: { step?: number | null; delivery?: number | null },
): boolean {
  if (row.bind_scope !== scope || (row.report_key ?? '') !== key) return false;
  if (scope === 'step' && ref?.step != null) return row.step === ref.step;
  if (scope === 'delivery' && ref?.delivery != null) return row.delivery === ref.delivery;
  return true;
}

/**
 * The campaign's *primary* report — `campaign` scope, empty key. This is what the delivery runner
 * renders by default and what every pre-binding row resolves as, so authoring an extra step-, delivery-
 * or global-bound report never leaks its rows into the SE's delivery report.
 */
export function isPrimaryReportRow(row: ReportBinding): boolean {
  return matchesReportBinding(row, 'campaign', '');
}

const statusLabels = new Map(CAMPAIGN_STATUSES.map((s) => [s.value, s.label]));
const trackLabels = new Map(STEP_TRACKS.map((t) => [t.value, t.label]));
const kindLabels = new Map(STEP_KINDS.map((k) => [k.value, k.label]));
const deliveryStatusLabels = new Map(DELIVERY_STATUSES.map((s) => [s.value, s.label]));
const stepStatusLabels = new Map(STEP_STATUSES.map((s) => [s.value, s.label]));
const actionItemStatusLabels = new Map(ACTION_ITEM_STATUSES.map((s) => [s.value, s.label]));

export const statusLabel = (status?: string): string =>
  (status && statusLabels.get(status as CampaignStatus)) || status || '—';

export const trackLabel = (track?: string): string =>
  (track && trackLabels.get(track as StepTrack)) || track || '—';

export const stepKindLabel = (kind?: string): string =>
  (kind && kindLabels.get(kind as StepKind)) || kind || '—';

export const deliveryStatusLabel = (status?: string): string =>
  (status && deliveryStatusLabels.get(status as DeliveryStatus)) || status || '—';

export const stepStatusLabel = (status?: string): string =>
  (status && stepStatusLabels.get(status as StepStatus)) || status || '—';

export const actionItemStatusLabel = (status?: string): string =>
  (status && actionItemStatusLabels.get(status as ActionItemStatus)) || status || '—';

/**
 * The three-value vocabulary the status chips render (done / in progress / not started), collapsed
 * from any of the delivery-side statuses. Ported from the PoC so the delivery board, targets and
 * action items all colour consistently.
 */
export type StatusTone = 'done' | 'progress' | 'notstarted';

export const toStatusTone = (
  status?: StepStatus | TargetStatus | DeliveryStatus | ActionItemStatus | string
): StatusTone => {
  switch (status) {
    case 'completed':
    case 'done':
      return 'done';
    case 'in_progress':
    case 'scheduled':
      return 'progress';
    default:
      // not_started, draft, skipped, cancelled, open, dropped, undefined
      return 'notstarted';
  }
};

/** MUI Chip colour for a collapsed status tone. */
export const statusToneColor = (
  tone: StatusTone
): 'success' | 'warning' | 'default' => {
  if (tone === 'done') return 'success';
  if (tone === 'progress') return 'warning';
  return 'default';
};

// ---------------------------------------------------------------------------
// Self-attest checklists (content / action_item / follow-up steps)
// ---------------------------------------------------------------------------

/**
 * A single self-report checklist item on a `content`/`action_item`/`followup` step. Mirrors the
 * `campaign-test` demo's authored lists (READINESS_CHECKS, WORKSHOP_READINESS, NEXT_STEPS): the SE
 * ticks each one off as they do it. `role` renders the demo's Must/Should/Nice tag; it does NOT
 * change completion — every item must be ticked for the step to read "done".
 */
export interface SelfAttestItem {
  key: string;
  label: string;
  hint?: string;
  role?: 'must' | 'should' | 'nice';
}

/** The self-attest checklist an author put on a step, read from `metadata.checklist`. */
export interface StepChecklist {
  lead?: string;
  items: SelfAttestItem[];
}

/** Step kinds that render a self-attest checklist body when one is authored in `metadata.checklist`. */
export const SELF_ATTEST_KINDS = new Set<StepKind>(['content', 'action_item', 'followup']);

/**
 * The self-attest checklist authored on a step, or null when there is none. Reads `metadata.checklist`
 * and tolerates a bare `metadata.checklist` array (items only) as well as the `{ lead, items }` shape.
 * Only meaningful for `SELF_ATTEST_KINDS`; other kinds keep their dedicated body.
 */
export const stepChecklist = (step: CampaignStep): StepChecklist | null => {
  if (!SELF_ATTEST_KINDS.has(step.step_kind)) return null;
  const raw = (step.metadata as Json | undefined)?.checklist;
  if (!raw) return null;
  const items: SelfAttestItem[] = Array.isArray(raw) ? raw : Array.isArray(raw.items) ? raw.items : [];
  const valid = items.filter((it) => it && typeof it.key === 'string' && typeof it.label === 'string');
  if (valid.length === 0) return null;
  return { lead: Array.isArray(raw) ? undefined : raw.lead, items: valid };
};

/** The keys the SE has ticked on a step, read from their enrollment's `metadata.selfAttest[stepId]`. */
export const selfAttestFor = (
  enrollment: CampaignEnrollment | null | undefined,
  stepId: number
): string[] => {
  const bag = (enrollment?.metadata as Json | undefined)?.selfAttest;
  const ticked = bag && bag[String(stepId)];
  return Array.isArray(ticked) ? (ticked as string[]) : [];
};

/**
 * Whether the SE has marked a journey_map step complete campaign-locally, read from their enrollment's
 * `metadata.journeyMapComplete[stepId]`. A Cyber Academy course opens in a NEW TAB and never reports
 * progress back to its `JourneyMapAssignment` (which the shared Enablement rollup owns and may
 * recompute), so this explicit "mark complete" marker is the reliable per-step completion signal for a
 * journey_map step in the runner. Read as a fallback AFTER the assignment signal, before the overall
 * enrollment rollup (see useCampaignStepStatus.leafTone).
 */
export const journeyMapCompleteFor = (
  enrollment: CampaignEnrollment | null | undefined,
  stepId: number
): boolean => {
  const bag = (enrollment?.metadata as Json | undefined)?.journeyMapComplete;
  return !!(bag && bag[String(stepId)]);
};

/**
 * Collapse a self-attest checklist + the SE's ticked keys into a status tone: every item ticked →
 * done, some → progress, none → not started. Ticks for keys no longer in the checklist are ignored,
 * so editing the checklist never leaves a step stuck "done".
 */
export const selfAttestTone = (checklist: StepChecklist, tickedKeys: string[]): StatusTone => {
  const ticked = new Set(tickedKeys);
  const done = checklist.items.filter((it) => ticked.has(it.key)).length;
  if (checklist.items.length > 0 && done === checklist.items.length) return 'done';
  return done > 0 ? 'progress' : 'notstarted';
};

// ---------------------------------------------------------------------------
// Lightweight questions (the `question` step kind)
// ---------------------------------------------------------------------------

/**
 * A single author-defined question on a `question` step. A trimmed alternative to a full Assessment
 * — definitions live in `metadata.questions`, answers in the SE's enrollment metadata — for
 * capturing the demo's free-form Q&A (discoveryAnswers) and prep/pre-req/deep-dive style questions
 * without the Assessment→Section→Question scaffolding. `type` mirrors the assessment response types
 * the runner already knows how to render via AssessmentQuestionInput.
 */
export type StepQuestionType =
  | 'text'
  | 'textarea'
  | 'boolean'
  | 'scale'
  | 'single_choice'
  | 'multi_choice'
  | 'number'
  | 'date';

export interface StepQuestionOption {
  value: string;
  label: string;
}

export interface StepQuestion {
  key: string;
  label: string;
  help?: string;
  type: StepQuestionType;
  /** For `single_choice` / `multi_choice`. */
  options?: StepQuestionOption[];
  required?: boolean;
}

/** The question set an author put on a step, read from `metadata.questions`. */
export interface StepQuestionSet {
  lead?: string;
  items: StepQuestion[];
}

/** Answer types the question inputs produce (single value, multi-select array, or boolean/number). */
export type StepQuestionAnswer = string | string[] | number | boolean | null;

const QUESTION_TYPES = new Set<StepQuestionType>([
  'text',
  'textarea',
  'boolean',
  'scale',
  'single_choice',
  'multi_choice',
  'number',
  'date',
]);

/**
 * Normalize a `single_choice` / `multi_choice` question's answer options to the UI's
 * `{ value, label }` shape. This is where the shape divergence between the two authoring paths is
 * reconciled so a choice question always renders its options:
 *   - the builder's `{ value, label }` — kept as-is;
 *   - the DB content-block `CampaignQuestionOption` (`{ value, cues, plays }`, no `label`) — the
 *     label falls back to `value`, since that is the only human-readable field it carries (`cues` /
 *     `plays` are deliberately not surfaced: the lightweight question body has no per-option slot for
 *     them, and inventing one is out of scope);
 *   - a bare string — treated as its own value and label.
 * Options with no usable string `value` are dropped.
 */
const toStepQuestionOptions = (raw: unknown[]): StepQuestionOption[] =>
  raw
    .map((o: any): StepQuestionOption | null => {
      if (typeof o === 'string') return { value: o, label: o };
      if (o && typeof o.value === 'string') {
        return {
          value: o.value,
          label: typeof o.label === 'string' && o.label ? o.label : o.value,
        };
      }
      return null;
    })
    .filter((o): o is StepQuestionOption => o != null);

/**
 * The question set authored on a `question` step, or null when there is none. Reads
 * `metadata.questions` and tolerates a bare array (items only) as well as `{ lead, items }`; each
 * item needs a string `key`/`label` and a known `type` (defaults to `text`). Choice options are
 * normalized via `toStepQuestionOptions`, so options authored in the content-block
 * `{ value, cues, plays }` shape render just as builder-authored `{ value, label }` options do. Only
 * meaningful for the `question` kind.
 */
export const stepQuestions = (step: CampaignStep): StepQuestionSet | null => {
  if (step.step_kind !== 'question') return null;
  const raw = (step.metadata as Json | undefined)?.questions;
  if (!raw) return null;
  const list: any[] = Array.isArray(raw) ? raw : Array.isArray(raw.items) ? raw.items : [];
  const items: StepQuestion[] = list
    .filter((q) => q && typeof q.key === 'string' && typeof q.label === 'string')
    .map((q) => ({
      key: q.key,
      label: q.label,
      help: typeof q.help === 'string' ? q.help : undefined,
      type: QUESTION_TYPES.has(q.type) ? q.type : 'text',
      options: Array.isArray(q.options) ? toStepQuestionOptions(q.options) : undefined,
      required: q.required === true,
    }));
  if (items.length === 0) return null;
  return { lead: Array.isArray(raw) ? undefined : raw.lead, items };
};

/** The SE's answers on a `question` step, read from their enrollment `metadata.questionAnswers[stepId]`. */
export const questionAnswersFor = (
  enrollment: CampaignEnrollment | null | undefined,
  stepId: number
): Record<string, StepQuestionAnswer> => {
  const bag = (enrollment?.metadata as Json | undefined)?.questionAnswers;
  const answers = bag && bag[String(stepId)];
  return answers && typeof answers === 'object' ? (answers as Record<string, StepQuestionAnswer>) : {};
};

/** Whether a single answer counts as provided (non-empty string, non-empty array, boolean, number). */
export const isQuestionAnswered = (answer: StepQuestionAnswer | undefined): boolean => {
  if (answer == null) return false;
  if (typeof answer === 'string') return answer.trim().length > 0;
  if (Array.isArray(answer)) return answer.length > 0;
  return true; // boolean / number
};

/**
 * Collapse a question set + the SE's answers into a status tone. If any questions are required, the
 * step is done when all required ones are answered; otherwise when all items are answered. Some
 * answered → progress, none → not started. Answers for keys no longer in the set are ignored, so
 * editing the questions never strands a step "done".
 */
export const questionTone = (
  set: StepQuestionSet,
  answers: Record<string, StepQuestionAnswer>
): StatusTone => {
  const answered = (q: StepQuestion) => isQuestionAnswered(answers[q.key]);
  const required = set.items.filter((q) => q.required);
  const target = required.length ? required : set.items;
  const doneCount = target.filter(answered).length;
  if (target.length > 0 && doneCount === target.length) return 'done';
  return set.items.some(answered) ? 'progress' : 'notstarted';
};

/** Sort by `order`, breaking ties by id, matching the backend's `('order', 'id')` ordering. */
export const byOrder = <T extends { order?: number; id?: number }>(rows: T[]): T[] =>
  [...(rows || [])].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0) || (a?.id ?? 0) - (b?.id ?? 0));

/**
 * Build the hierarchical step tree from a flat `CampaignStep[]`, using each step's `parent`/`order`.
 *
 * Steps are fetched flat (`GET /api/db/campaign-step/?campaign=<id>`) and the tree is assembled on
 * the client, so recursive nested serializers are never needed. Roots are steps with no parent (or
 * a parent outside the fetched set); every level is `byOrder`-sorted. A cycle-safe walk guards
 * against a step that (through bad data) references itself as an ancestor.
 */
export const buildStepTree = (steps: CampaignStep[]): CampaignStepNode[] => {
  const nodes = new Map<number, CampaignStepNode>();
  (steps || []).forEach((s) => nodes.set(s.id, { ...s, children: [] }));

  const roots: CampaignStepNode[] = [];
  nodes.forEach((node) => {
    const parent = node.parent != null ? nodes.get(node.parent) : undefined;
    if (parent && parent.id !== node.id) parent.children.push(node);
    else roots.push(node);
  });

  const sortTree = (list: CampaignStepNode[]): CampaignStepNode[] => {
    const sorted = byOrder(list);
    sorted.forEach((n) => {
      n.children = sortTree(n.children);
    });
    return sorted;
  };

  return sortTree(roots);
};

/**
 * The steps a runner should show: drops any step flagged `hidden`, and any step whose ancestor group
 * is hidden — hiding a group hides its whole subtree (buildStepTree would otherwise orphan a hidden
 * group's children up to the root). Authoring surfaces keep the full list and filter nothing.
 */
export const visibleSteps = (steps: CampaignStep[]): CampaignStep[] => {
  const byId = new Map<number, CampaignStep>((steps || []).map((s) => [s.id, s]));
  const resolved = new Map<number, boolean>();
  const isHidden = (step: CampaignStep): boolean => {
    const cached = resolved.get(step.id);
    if (cached != null) return cached;
    // Mark in-progress as visible first, so a malformed parent cycle resolves rather than recursing.
    resolved.set(step.id, false);
    let hidden = !!step.hidden;
    if (!hidden && step.parent != null) {
      const parent = byId.get(step.parent);
      if (parent && parent.id !== step.id) hidden = isHidden(parent);
    }
    resolved.set(step.id, hidden);
    return hidden;
  };
  return (steps || []).filter((s) => !isHidden(s));
};

/**
 * The chain of steps from the root down to (and including) `stepId`, for a breadcrumb trail.
 *
 * Walks the flat step list by `parent`, cycle-guarded, and returns the ancestors ordered
 * root → … → the step itself. Returns an empty array if the step is not in the set.
 */
export const stepTrail = (steps: CampaignStep[], stepId: number): CampaignStep[] => {
  const byId = new Map<number, CampaignStep>((steps || []).map((s) => [s.id, s]));
  const trail: CampaignStep[] = [];
  const seen = new Set<number>();
  let current: CampaignStep | undefined = byId.get(stepId);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    trail.unshift(current);
    current = current.parent != null ? byId.get(current.parent) : undefined;
  }
  return trail;
};

/**
 * A top-level per-delivery group — the deliveries surface.
 *
 * `completion_mode` lives only on a top-level step (the builder enforces this), so a `per_delivery`
 * group at the root is delivered repeatedly: the runner shows the SE's deliveries here and walks this
 * group's sub-steps once per delivery, rather than treating it as a single one-time stop. Mirrors the
 * reference workshop-campaign, where the "workshop list" is one stop and each workshop walks the same
 * per-delivery steps.
 */
export const isPerDeliveryGroup = (step: {
  parent: number | null;
  step_kind: StepKind;
  completion_mode?: CompletionMode | null;
}): boolean =>
  step.parent == null && step.step_kind === 'group' && step.completion_mode === 'per_delivery';

/**
 * URL for a runner step. When `deliveryId` is given the step is scoped to that delivery
 * (`/campaigns/:id/deliveries/:deliveryId/steps/:stepId`) — the path form used while walking a
 * per-delivery group's sub-steps, so the delivery context survives refresh, deep links and the Back
 * button. Without it, the plain campaign-level step path.
 */
export const campaignStepPath = (
  campaignId: number | string,
  stepId: number | string,
  deliveryId?: number | string | null
): string =>
  deliveryId != null
    ? `/campaigns/${campaignId}/deliveries/${deliveryId}/steps/${stepId}`
    : `/campaigns/${campaignId}/steps/${stepId}`;

/**
 * The linear sequence of navigable "stops" for the campaign-level runner.
 *
 * Top-level groups are PHASES, not stops: the runner lands on their leaf descendants, never a
 * container page (mirroring the reference flow). The one exception is a top-level per-delivery group,
 * which IS a single stop — the deliveries list; its sub-steps are walked per delivery (see
 * `deliveryStops`) and so are deliberately excluded from this campaign-level sequence.
 */
export const campaignStops = (tree: CampaignStepNode[]): CampaignStepNode[] => {
  const out: CampaignStepNode[] = [];
  const pushLeaves = (node: CampaignStepNode) => {
    node.children.forEach((child) => {
      if (child.children.length) pushLeaves(child);
      else out.push(child);
    });
  };
  tree.forEach((root) => {
    if (isPerDeliveryGroup(root)) out.push(root);
    else if (root.step_kind === 'group' && root.children.length) pushLeaves(root);
    else out.push(root);
  });
  return out;
};

/**
 * The leaf sub-steps a single delivery walks through, for a per-delivery group — its "top level
 * steps" in reference-flow terms. Nested groups under the delivery group are flattened to their
 * leaves too, so the delivery walk is always a flat ordered list.
 */
export const deliveryStops = (group: CampaignStepNode): CampaignStepNode[] => {
  const out: CampaignStepNode[] = [];
  const walk = (node: CampaignStepNode) =>
    node.children.forEach((child) =>
      child.children.length ? walk(child) : out.push(child)
    );
  walk(group);
  return out;
};

/**
 * The campaign's consolidated first-step gate, if one is authored: the step flagged
 * `metadata.gatesCampaign === true`. Its self-attest checklist (metadata.checklist) must be fully
 * ticked before the runner lets the SE reach any other step — the portal analogue of the reference
 * app's up-front training gate (BUILD.md §4, §6). At most one is expected; the first found wins.
 * Returns null when no step declares the flag (the common case — nothing gates).
 */
export const campaignGateStep = (steps: CampaignStep[]): CampaignStep | null =>
  (steps || []).find((s) => (s.metadata as Json | undefined)?.gatesCampaign === true) ?? null;

/**
 * Whether a step can register its OWN completion — reach tone `done` from a per-step signal rather
 * than only when the whole campaign is complete. This is the set of steps that may gate forward
 * progression when marked `is_required`: gating a required step with no completion signal (e.g. a
 * `content` page without a self-attest checklist) would hard-lock the runner, since its tone stays
 * `notstarted` until everything else finishes. Mirrors the per-kind signals in
 * useCampaignStepStatus.leafTone.
 */
export const stepCanSelfComplete = (step: CampaignStep): boolean => {
  switch (step.step_kind) {
    case 'assessment':
      return step.assessment != null;
    case 'question':
      return stepQuestions(step) != null;
    case 'salesforce_link':
      return true;
    case 'journey_map':
      return step.journey_map != null;
    case 'moodle_workshop':
      return true;
    case 'group':
      // A group's tone aggregates its children; requiring it gates on their combined completion.
      return true;
    default:
      // content / action_item / followup: only a self-attest checklist gives a completion signal.
      return stepChecklist(step) != null;
  }
};

/** Immediate children of `parentId` (or the roots when `parentId` is null), `byOrder`-sorted. */
export const childrenOf = (steps: CampaignStep[], parentId: number | null): CampaignStep[] =>
  byOrder((steps || []).filter((s) => (s.parent ?? null) === parentId));

/** The next `order` value for a new child under `parentId` (max existing + 1, else 0). */
export const nextOrder = (steps: CampaignStep[], parentId: number | null): number => {
  const siblings = childrenOf(steps, parentId);
  return siblings.length ? Math.max(...siblings.map((s) => s.order ?? 0)) + 1 : 0;
};

/**
 * The campaign's selected theme, or null when none is authored (fall back to the default tokens).
 * Reads `Campaign.metadata.theme` and validates it against the known set.
 */
export const campaignTheme = (campaign: Pick<Campaign, 'metadata'> | null | undefined): CampaignTheme | null => {
  const raw = (campaign?.metadata as Json | undefined)?.theme;
  return raw === 'harbor' || raw === 'harbor-dark' || raw === 'ai-era' ? raw : null;
};

/**
 * The author-supplied help/resource links on a campaign or step, read from `metadata.helpLinks`.
 * Returns only well-formed `{ label, url }` entries; an empty array means "render no help control".
 */
export const helpLinksOf = (
  owner: { metadata?: Json } | null | undefined
): HelpLink[] => {
  const raw = (owner?.metadata as Json | undefined)?.helpLinks;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((l) => l && typeof l.label === 'string' && typeof l.url === 'string' && l.url.trim())
    .map((l) => ({ label: l.label, url: l.url }));
};

/**
 * Whether a per-delivery step is Salesforce Account/Opportunity–driven (the SE picks a real account
 * per delivery) vs. free-titled (the SE names a one-to-many event). Read from
 * `metadata.salesforceDriven`; defaults to true so existing per-delivery steps keep the SF picker.
 */
export const stepSalesforceDriven = (step: { metadata?: Json } | null | undefined): boolean => {
  const raw = (step?.metadata as Json | undefined)?.salesforceDriven;
  return raw == null ? true : raw === true;
};

// ---------------------------------------------------------------------------
// File uploads — an optional per-delivery document collection ADDED TO a step
// ---------------------------------------------------------------------------
//
// File uploads are not a step kind. Any content-bearing step (content / action_item / followup) can
// opt into a file-upload section by carrying a `metadata.fileUpload` config; the runner renders the
// uploader in that step's body when the config is present. Files are stored per delivery on the
// delivery's `CampaignDeliveryStepStatus.metadata.uploads` and fetched through a campaign-access
// -gated download endpoint (see support/campaignFiles.ts).

/**
 * The file-type restriction categories an author can allow on a step's file-upload section. Each
 * maps to a set of extensions in support/campaignFiles.ts (the runtime `accept`/validation source):
 *   - documents     → Word/Excel (doc, docx, xls, xlsx)
 *   - text          → txt and text-data variants (md, json, yaml/yml, xml, csv)
 *   - code          → source files (py, js, ts, go, …)
 *   - presentations → PowerPoint (ppt, pptx)
 *   - pdf           → pdf
 *   - archives      → zip
 *   - any           → no restriction (overrides all others)
 */
export type FileTypeCategory =
  | 'documents'
  | 'text'
  | 'code'
  | 'presentations'
  | 'pdf'
  | 'archives'
  | 'any';

export const FILE_TYPE_CATEGORIES: { value: FileTypeCategory; label: string; help: string }[] = [
  { value: 'documents', label: 'Documents', help: 'Word & Excel — doc, docx, xls, xlsx' },
  { value: 'text', label: 'Text & data', help: 'txt, md, json, yaml, xml, csv' },
  { value: 'code', label: 'Code files', help: 'Source files — py, js, ts, go, …' },
  { value: 'presentations', label: 'Presentations', help: 'PowerPoint — ppt, pptx' },
  { value: 'pdf', label: 'PDF', help: 'pdf' },
  { value: 'archives', label: 'Archives', help: 'zip' },
  { value: 'any', label: 'Any file type', help: 'No restriction — allows every file type' },
];

const FILE_TYPE_CATEGORY_VALUES = new Set<FileTypeCategory>(
  FILE_TYPE_CATEGORIES.map((c) => c.value)
);

/** The author-side configuration of a step's file-upload section, read from `metadata.fileUpload`. */
export interface StepFileUploadConfig {
  /** Section heading shown above the uploader (context: what files). */
  title: string;
  /** Longer prose shown under the heading (context: why they are needed). Markdown-rendered. */
  description: string;
  /** Allowed file-type categories; `['any']` means no restriction. Never empty. */
  allowedTypes: FileTypeCategory[];
  /**
   * When true, the step is not complete (and the runner blocks advancing past it) until at least one
   * file has been uploaded for the delivery. When false the uploader is purely optional. Defaults to
   * false. The gate is only enforced in a delivery context, where uploads actually happen — see
   * `fileUploadRequirementMet`.
   */
  required: boolean;
}

/**
 * One uploaded file's record, stored on the delivery's CampaignDeliveryStepStatus
 * `metadata.uploads` array. `key` is the raw S3 object_key returned by the presign — it is only
 * ever fetched back through the campaign-access-gated download endpoint, never exposed as a URL.
 */
export interface DeliveryFileUpload {
  key: string;
  name: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string;
  uploaded_by: string | null;
}

/**
 * Read a step's file-upload section config from `metadata.fileUpload`, or `null` when the step has
 * not opted into a file-upload section (the presence of the key IS the "enabled" flag). Missing/
 * blank fields degrade to empty strings; an absent or empty `allowedTypes` collapses to `['any']`
 * so an enabled-but-unrestricted section still accepts uploads rather than rejecting everything.
 */
export const stepFileUploadConfig = (
  step: { metadata?: Json } | null | undefined
): StepFileUploadConfig | null => {
  const raw = (step?.metadata as Json | undefined)?.fileUpload as Json | undefined;
  if (!raw || typeof raw !== 'object') return null;
  const title = typeof raw.title === 'string' ? raw.title : '';
  const description = typeof raw.description === 'string' ? raw.description : '';
  const rawTypes = Array.isArray(raw.allowedTypes) ? raw.allowedTypes : [];
  const allowed = rawTypes.filter((t): t is FileTypeCategory =>
    FILE_TYPE_CATEGORY_VALUES.has(t as FileTypeCategory)
  );
  return {
    title,
    description,
    allowedTypes: allowed.length ? Array.from(new Set(allowed)) : ['any'],
    required: raw.required === true,
  };
};

/**
 * Whether a step's file-upload requirement is satisfied for a delivery: trivially true when the
 * section is optional (or absent), otherwise true only once at least one file has been uploaded.
 * The gate is per-delivery — outside a delivery context there is nowhere to upload, so callers pass
 * `inDelivery=false` and the requirement is treated as not-applicable (never a hard-lock).
 */
export const fileUploadRequirementMet = (
  config: StepFileUploadConfig | null,
  uploadCount: number,
  inDelivery: boolean
): boolean => {
  if (!config || !config.required || !inDelivery) return true;
  return uploadCount > 0;
};

/**
 * The uploaded-file records on a delivery step-status row's `metadata.uploads`. Returns only
 * well-formed entries (a `key` and `name` at minimum), so a partially written row is safe to read.
 */
export const deliveryUploads = (
  row: { metadata?: Json } | null | undefined
): DeliveryFileUpload[] => {
  const raw = (row?.metadata as Json | undefined)?.uploads;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((u) => u && typeof u.key === 'string' && typeof u.name === 'string')
    .map((u) => ({
      key: u.key,
      name: u.name,
      content_type: typeof u.content_type === 'string' ? u.content_type : '',
      size_bytes: typeof u.size_bytes === 'number' ? u.size_bytes : 0,
      uploaded_at: typeof u.uploaded_at === 'string' ? u.uploaded_at : '',
      uploaded_by: typeof u.uploaded_by === 'string' ? u.uploaded_by : null,
    }));
};

/** A stable, human-ish key from a title/name, for pre-filling the `key`/`slug` field in the builder. */
export const slugifyKey = (input: string): string =>
  (input || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);

/** A unique, auto-generated key for a new campaign/step, avoiding the given `taken` set. */
export const uniqueKey = (base: string, taken: Iterable<string>): string => {
  const used = new Set(taken);
  const root = slugifyKey(base) || `item-${Date.now().toString(36)}`;
  if (!used.has(root)) return root;
  for (let i = 2; i < 1000; i += 1) {
    const suffix = `-${i}`;
    const candidate = `${root.slice(0, 100 - suffix.length)}${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
  const suffix = `-${Date.now().toString(36)}`;
  return `${root.slice(0, 100 - suffix.length)}${suffix}`;
};
